// - - - - - - - - - -
// #174 — format a logged-in user's display name into Brandon's lock-attribution
// form: last name + first initial, e.g. "Leland Long" → "Long, L".
//
// The display name comes from the MSAL profile (`mongoUser._profile.data.name`),
// which is a free-form full name. Pure + tested so the lock popup, the unlock
// log, and the EventLog audit entry all format the actor identically.

/**
 * @param {string} displayName - full display name (e.g. "Leland Long")
 * @returns {string} "Last, F" (e.g. "Long, L"); "" when unusable; the single
 *   token unchanged when only one name part is present (no initial to derive).
 */
export function formatLockedBy(displayName) {
  if (!displayName || typeof displayName !== "string") return "";
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0];
  const first = parts[0];
  const last = parts[parts.length - 1];
  const initial = first.charAt(0).toUpperCase();
  return `${last}, ${initial}`;
}

/**
 * #174 — preferred formatter: build "Last, F" from the MSAL profile fields.
 *
 * Azure AD `given_name` / `family_name` (ID-token claims) give an EXACT
 * "Last, F" regardless of how the org formats the display `name` ("First Last"
 * vs "Last, First"). Falls back to parsing the display name when the discrete
 * name claims aren't present. Returns "" only when nothing usable exists
 * (the popup then says "another user").
 *
 * @param {Object} profile - mongoUser._profile.data
 *   { givenName?, familyName?, name? }
 * @returns {string} "Last, F"
 */
export function formatLockedByFromProfile(profile) {
  if (!profile || typeof profile !== "object") return "";
  const given = typeof profile.givenName === "string" ? profile.givenName.trim() : "";
  const family = typeof profile.familyName === "string" ? profile.familyName.trim() : "";
  if (family && given) {
    return `${family}, ${given.charAt(0).toUpperCase()}`;
  }
  if (family) return family;
  // No discrete name claims — parse the display name ("First Last" → "Last, F").
  // Guard against the "Unknown" placeholder.
  if (profile.name && profile.name !== "Unknown") {
    return formatLockedBy(profile.name);
  }
  // #174 (2026-06-03) — the app has NO reliable first/last name: the name
  // field broke in the Realm→TanStack migration, the Users doc only stores the
  // email, and this MSAL access-token flow doesn't surface name claims. Fall
  // back to the email username (local-part) — the SAME identifier the rest of
  // the app uses for "who did what" (history strings, EventLog, Time Machine
  // bubbles). If real names ever become available (e.g. ID-token scopes or a
  // Users-doc name field), the given/family/name branches above auto-upgrade
  // this to "Last, F" with no further change here.
  const email = typeof profile.email === "string" ? profile.email.trim() : "";
  if (email && email !== "Unknown") {
    return email.includes("@") ? email.split("@")[0] : email;
  }
  return "";
}

export default formatLockedBy;
